import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/api/client';
import { RMA_STATUSES } from '@/utils/helpers';
import { Search, Sparkles, Loader2, Users as UsersIcon } from 'lucide-react';
import { today } from '@/utils/helpers';
import { SupplierRMAWorkflow } from '@/components/forms/SupplierRMAWorkflow';

const emptyPurchaseInfo = () => ({
  invoice_id: '',
  invoice_number: '',
  invoice_date: '',
  product_name: '',
  brand: '',
  model: '',
  warranty_months: 0,
  purchase_date: '',
  customer_name: '',
  customer_phone: '',
  sale_price: 0
});

const emptyForm = () => ({
  rma_type: 'Customer',
  supplier_name: '',
  customer_id: '',
  product_id: '',
  serial_original: '',
  serial_replacement: '',
  replacement_product_id: '',
  replacement_product_name: '',
  replacement_brand: '',
  replacement_model: '',
  replacement_added_to_inventory: false,
  status: 'Pending',
  issue: '',
  tech_notes: '',
  standby_serial: '',
  standby_product_id: '',
  standby_product_name: '',
  standby_returned: false,
  supplier_rma: false,
  purchase_info: emptyPurchaseInfo(),
  // Accountability
  assigned_technician_id: '',
  assigned_technician_name: '',
  tested_by_id: '',
  tested_by_name: '',
  test_date: '',
  diagnosis_notes: '',
  repaired_by_id: '',
  repaired_by_name: '',
  replacement_processed_by_id: '',
  replacement_processed_by_name: '',
  credit_note_processed_by_id: '',
  credit_note_processed_by_name: ''
});

export const RMAForm = ({ isOpen, onClose, rma = null, onSaved }) => {
  const [currentRma, setCurrentRma] = useState(rma);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupMessage, setLookupMessage] = useState('');
  const [showReplacement, setShowReplacement] = useState(false);
  const lookupTimer = useRef(null);

  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    if (isOpen) {
      loadData();
      setCurrentRma(rma);
      if (rma) {
        setFormData({
          rma_type: rma.rma_type || 'Customer',
          supplier_name: rma.supplier_name || '',
          customer_id: rma.customer_id || '',
          product_id: rma.product_id || '',
          serial_original: rma.serial_original || '',
          serial_replacement: rma.serial_replacement || '',
          replacement_product_id: rma.replacement_product_id || '',
          replacement_product_name: rma.replacement_product_name || '',
          replacement_brand: rma.replacement_brand || '',
          replacement_model: rma.replacement_model || '',
          replacement_added_to_inventory: rma.replacement_added_to_inventory || false,
          status: rma.status || 'Pending',
          issue: rma.issue || '',
          tech_notes: rma.tech_notes || '',
          standby_serial: rma.standby_serial || '',
          standby_product_id: rma.standby_product_id || '',
          standby_product_name: rma.standby_product_name || '',
          standby_returned: rma.standby_returned || false,
          supplier_rma: rma.supplier_rma || false,
          purchase_info: rma.purchase_info || emptyPurchaseInfo(),
          assigned_technician_id: rma.assigned_technician_id || '',
          assigned_technician_name: rma.assigned_technician_name || '',
          tested_by_id: rma.tested_by_id || '',
          tested_by_name: rma.tested_by_name || '',
          test_date: rma.test_date || '',
          diagnosis_notes: rma.diagnosis_notes || '',
          repaired_by_id: rma.repaired_by_id || '',
          repaired_by_name: rma.repaired_by_name || '',
          replacement_processed_by_id: rma.replacement_processed_by_id || '',
          replacement_processed_by_name: rma.replacement_processed_by_name || '',
          credit_note_processed_by_id: rma.credit_note_processed_by_id || '',
          credit_note_processed_by_name: rma.credit_note_processed_by_name || ''
        });
        setShowReplacement(!!(rma.serial_replacement || rma.replacement_product_name));
      } else {
        setFormData(emptyForm());
        setShowReplacement(false);
      }
      setLookupMessage('');
    }
  }, [isOpen, rma]);

  const loadData = async () => {
    try {
      const [custRes, prodRes, userRes] = await Promise.all([
        api.getCustomers(),
        api.getProducts(),
        api.getActiveUsers().catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data);
      setProducts(prodRes.data);
      setUsers((userRes.data || []).filter(u => u.active !== false));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Auto-lookup when the original serial changes (debounced).
  const handleSerialChange = (val) => {
    setFormData((prev) => ({ ...prev, serial_original: val }));
    setLookupMessage('');
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (!val || val.length < 3) return;
    lookupTimer.current = setTimeout(() => runPurchaseLookup(val), 500);
  };

  const runPurchaseLookup = async (serial) => {
    setLookingUp(true);
    try {
      const res = await api.lookupRMAPurchase(serial);
      const { product, customer, invoice, purchase_info, found } = res.data || {};
      if (!found) {
        setLookupMessage('No purchase record found for this serial.');
        setLookingUp(false);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        customer_id: customer?.id || prev.customer_id,
        product_id: product?.id || prev.product_id,
        purchase_info: purchase_info || prev.purchase_info,
      }));
      const parts = [];
      if (purchase_info?.invoice_number) parts.push(`Invoice ${purchase_info.invoice_number}`);
      if (purchase_info?.product_name) parts.push(purchase_info.product_name);
      if (purchase_info?.customer_name) parts.push(purchase_info.customer_name);
      setLookupMessage(parts.length ? `Auto-filled: ${parts.join(' · ')}` : 'Auto-filled purchase info');
    } catch (e) {
      setLookupMessage('Lookup failed');
    } finally {
      setLookingUp(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone) {
      alert('Name and phone required');
      return;
    }
    try {
      const res = await api.createCustomer(newCustomer);
      setCustomers([...customers, res.data]);
      setFormData({ ...formData, customer_id: res.data.id });
      setShowNewCustomer(false);
      setNewCustomer({ name: '', phone: '', email: '' });
    } catch (e) { alert('Failed'); }
  };

  const selectedProduct = products.find(p => p.id === formData.product_id);

  const handleReplacementProductSelect = (productId) => {
    const p = products.find(x => x.id === productId);
    setFormData({
      ...formData,
      replacement_product_id: productId,
      replacement_product_name: p?.name || '',
      replacement_brand: p?.brand || '',
      replacement_model: p?.model || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isInternal = formData.rma_type === 'Internal';
    if (!formData.product_id || !formData.serial_original || !formData.issue) {
      alert('Please fill in product, original serial, and issue');
      return;
    }
    if (!isInternal && !formData.customer_id) {
      alert('Please select a customer for a Customer RMA');
      return;
    }
    // If user opted to add replacement to inventory, confirm
    if (formData.replacement_added_to_inventory && formData.serial_replacement) {
      const ok = window.confirm(
        `Add replacement serial "${formData.serial_replacement}" to inventory now?\n` +
        `Product: ${formData.replacement_product_name || '(new item)'}\n` +
        (formData.replacement_brand ? `Brand: ${formData.replacement_brand}\n` : '') +
        (formData.replacement_model ? `Model: ${formData.replacement_model}` : '')
      );
      if (!ok) return;
    }

    setLoading(true);
    try {
      if (rma) {
        await api.updateRMA(rma.id, formData);
      } else {
        await api.createRMA(formData);
      }
      onSaved && onSaved();
      onClose();
    } catch (error) {
      alert('Failed to save RMA');
    } finally { setLoading(false); }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone.includes(customerSearch)
  );

  const pi = formData.purchase_info || emptyPurchaseInfo();
  const hasPurchaseInfo = !!(pi.invoice_id || pi.invoice_number || pi.product_name || pi.customer_name);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={rma ? `Edit RMA ${rma.job_id || ''}` : 'Create New RMA'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* RMA Type Toggle */}
        <div className="flex items-center gap-3 p-3 bg-[var(--surface-hover)] rounded-lg border border-[var(--border)]" data-testid="rma-type-toggle">
          <label className="text-sm font-semibold text-[var(--text-primary)]">RMA Type:</label>
          <div className="flex rounded-md overflow-hidden border border-[var(--border)]">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, rma_type: 'Customer' })}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${formData.rma_type === 'Customer' ? 'bg-blue-600 text-white' : 'bg-transparent text-[var(--text-muted)] hover:bg-[var(--surface)]'}`}
              data-testid="rma-type-customer"
            >
              Customer (sold item)
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, rma_type: 'Internal' })}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${formData.rma_type === 'Internal' ? 'bg-amber-600 text-white' : 'bg-transparent text-[var(--text-muted)] hover:bg-[var(--surface)]'}`}
              data-testid="rma-type-internal"
            >
              Internal (stock / build)
            </button>
          </div>
          <p className="text-xs text-[var(--text-muted)] ml-2">
            {formData.rma_type === 'Internal'
              ? 'For DOA / faulty stock not yet sold to a customer.'
              : 'For items already sold (RMA against a customer purchase).'}
          </p>
        </div>

        {/* Original Serial - moved to top so users scan first */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
          <label className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 block">
            Original Serial Number *
          </label>
          <div className="relative">
            <Input
              value={formData.serial_original}
              onChange={(e) => handleSerialChange(e.target.value)}
              placeholder="Scan or enter the original serial number…"
              data-testid="rma-serial-original"
              className="pr-10"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {lookingUp ? <Loader2 className="animate-spin text-blue-600" size={18} /> : <Sparkles className="text-blue-500" size={18} />}
            </div>
          </div>
          {lookupMessage && (
            <p className="text-xs mt-2 text-blue-700 dark:text-blue-300" data-testid="rma-lookup-message">{lookupMessage}</p>
          )}
        </div>

        {/* Auto-filled Purchase Info Panel */}
        {hasPurchaseInfo && (
          <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700" data-testid="rma-purchase-info-panel">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-2">
              <Sparkles size={14} className="text-cyan-500" />
              Purchase Information (auto-filled)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-xs">
              <Field label="Customer" value={pi.customer_name} />
              <Field label="Phone" value={pi.customer_phone} />
              <Field label="Invoice #" value={pi.invoice_number} />
              <Field label="Invoice Date" value={pi.invoice_date} />
              <Field label="Purchase Date" value={pi.purchase_date} />
              <Field label="Product" value={pi.product_name} />
              <Field label="Brand" value={pi.brand} />
              <Field label="Model" value={pi.model} />
              <Field label="Warranty" value={pi.warranty_months ? `${pi.warranty_months} months` : '—'} />
            </div>
          </div>
        )}

        {/* Customer (hidden when Internal) */}
        {formData.rma_type === 'Customer' ? (
          <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
            <div className="flex justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Customer *</label>
              <Button type="button" size="sm" variant="ghost" onClick={() => setShowNewCustomer(!showNewCustomer)}>
                {showNewCustomer ? 'Select Existing' : '+ New Customer'}
              </Button>
            </div>
            {showNewCustomer ? (
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Name *" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} data-testid="rma-new-customer-name" />
                <Input placeholder="Phone *" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} data-testid="rma-new-customer-phone" />
                <div className="flex gap-1">
                  <Input placeholder="Email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} />
                  <Button type="button" size="sm" onClick={handleCreateCustomer}>Add</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                  <Input placeholder="Search..." value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="pl-9" />
                </div>
                <Select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} data-testid="rma-customer-select">
                  <option value="">-- Select Customer --</option>
                  {filteredCustomers.map(c => <option key={c.id} value={c.id}>{`${c.name} (${c.phone})`}</option>)}
                </Select>
              </>
            )}
          </div>
        ) : (
          <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
            <label className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2 block">Supplier (for internal/stock RMA)</label>
            <Input
              value={formData.supplier_name}
              onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
              placeholder="e.g. ASUS Distributor, Local Wholesaler..."
              data-testid="rma-supplier-name"
            />
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
              No customer is recorded for an Internal RMA. The faulty item stays under your store&apos;s warranty claim.
            </p>
          </div>
        )}

        {/* Product & Status */}
        <div className="grid grid-cols-2 gap-4">
          <Select label="Product *" value={formData.product_id} onChange={(e) => setFormData({ ...formData, product_id: e.target.value })} data-testid="rma-product-select">
            <option value="">-- Select Product --</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} data-testid="rma-status-select">
            {RMA_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>

        <Textarea label="Issue Reported *" value={formData.issue} onChange={(e) => setFormData({ ...formData, issue: e.target.value })} rows={3} data-testid="rma-issue" />
        <Textarea label="Tech Notes / Notes" value={formData.tech_notes} onChange={(e) => setFormData({ ...formData, tech_notes: e.target.value })} rows={2} data-testid="rma-tech-notes" placeholder="e.g. Manufacturer supplied a different replacement model due to original being discontinued." />

        {/* Initial Inspection & Job Note */}
        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
          <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-200 mb-3 flex items-center gap-2">
            <UsersIcon size={14} /> Initial Inspection
          </h3>
          <p className="text-xs text-[var(--text-muted)] mb-3">
            The Job Note will automatically record <strong>you</strong> as the person who prepared it. Add who tested the unit before taking it in.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <UserPickField
              label="Tested By (before taking in)"
              users={users}
              userId={formData.tested_by_id}
              userName={formData.tested_by_name}
              onChange={(id, name) => setFormData({
                ...formData,
                tested_by_id: id,
                tested_by_name: name,
                test_date: formData.test_date || (id ? today() : '')
              })}
              testId="rma-tested-by"
            />
            <Input
              label="Test Date"
              type="date"
              value={formData.test_date}
              onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
              data-testid="rma-test-date"
            />
          </div>
          <Textarea
            label="Diagnosis / Inspection Notes"
            value={formData.diagnosis_notes}
            onChange={(e) => setFormData({ ...formData, diagnosis_notes: e.target.value })}
            rows={2}
            placeholder='e.g. "No display output. Fault confirmed on GPU memory."'
            data-testid="rma-diagnosis"
          />
        </div>

        {/* Standby Unit Management */}
        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
          <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-200 mb-2">Standby Unit (Loaner)</h3>
          <p className="text-xs text-orange-800 dark:text-orange-300 mb-3">
            If you&apos;re lending a standby unit to the customer, pick the product and serial from inventory.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Standby Product</label>
              <select
                value={formData.standby_product_id}
                onChange={(e) => {
                  const pid = e.target.value;
                  const p = products.find(x => x.id === pid);
                  setFormData({
                    ...formData,
                    standby_product_id: pid,
                    standby_product_name: p?.name || '',
                    standby_serial: ''  // reset when product changes
                  });
                }}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="rma-standby-product"
              >
                <option value="">— No Standby —</option>
                {products.map(p => {
                  const inStockCount = (p.serials || []).filter(s => s.status === 'In Stock').length;
                  return (
                    <option key={p.id} value={p.id} disabled={inStockCount === 0 && p.id !== formData.standby_product_id}>
                      {`${p.name}${p.brand ? ` · ${p.brand}` : ''}${p.model ? ` ${p.model}` : ''} (${inStockCount} in stock)`}
                    </option>
                  );
                })}
              </select>
              {formData.standby_product_id && (() => {
                const sp = products.find(p => p.id === formData.standby_product_id);
                const available = sp ? (sp.serials || []).filter(s => s.status === 'In Stock').length : 0;
                return (
                  <p className="text-[10px] mt-1 text-orange-700 dark:text-orange-300">
                    {available} unit{available !== 1 ? 's' : ''} available in inventory
                  </p>
                );
              })()}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Standby Serial Number</label>
              <select
                value={formData.standby_serial}
                onChange={(e) => setFormData({ ...formData, standby_serial: e.target.value })}
                disabled={!formData.standby_product_id}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                data-testid="rma-standby-serial"
              >
                <option value="">— Select a Serial —</option>
                {(() => {
                  const sp = products.find(p => p.id === formData.standby_product_id);
                  if (!sp) return null;
                  return (sp.serials || [])
                    .filter(s => s.status === 'In Stock' || s.sn === formData.standby_serial)
                    .map(s => <option key={s.sn} value={s.sn}>{`${s.sn} (${s.status})`}</option>);
                })()}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer mt-3">
            <input
              type="checkbox"
              checked={formData.standby_returned}
              onChange={(e) => setFormData({ ...formData, standby_returned: e.target.checked })}
              className="w-4 h-4"
              data-testid="rma-standby-returned"
              disabled={!formData.standby_serial}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Standby Returned</span>
          </label>
        </div>

        {/* Supplier RMA Lifecycle — visible only after RMA has been created */}
        {currentRma && (
          <SupplierRMAWorkflow
            rma={currentRma}
            products={products}
            onRefresh={async () => {
              try {
                const res = await api.getRMAs();
                const fresh = (res.data || []).find(r => r.id === currentRma.id);
                if (fresh) {
                  setCurrentRma(fresh);
                  setFormData(prev => ({
                    ...prev,
                    status: fresh.status || prev.status,
                    serial_replacement: fresh.serial_replacement || prev.serial_replacement,
                  }));
                }
                onSaved && onSaved();
              } catch (e) { /* ignore */ }
            }}
          />
        )}

        {/* Supplier RMA */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.supplier_rma}
            onChange={(e) => setFormData({ ...formData, supplier_rma: e.target.checked })}
            className="w-4 h-4"
            data-testid="rma-supplier-rma"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Escalated to Supplier</span>
        </label>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={loading} data-testid="save-rma">
            {loading ? 'Saving...' : (rma ? 'Update RMA' : 'Create RMA')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{value || '—'}</div>
  </div>
);

const UserPickField = ({ label, users, userId, userName, onChange, testId }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    <select
      value={userId}
      onChange={(e) => {
        const id = e.target.value;
        const u = users.find(x => x.id === id);
        onChange(id, u ? (u.full_name || u.username) : '');
      }}
      className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      data-testid={testId}
    >
      <option value="">— Unassigned —</option>
      {users.map(u => (
        <option key={u.id} value={u.id}>
          {(u.full_name || u.username)}{u.role ? ` · ${u.role}` : ''}
        </option>
      ))}
    </select>
    {!userId && userName && (
      <p className="text-[10px] mt-1 text-gray-500">Legacy: {userName}</p>
    )}
  </div>
);
